import { useCallback, useEffect, useState } from "react";

import {
  fetchDashboardSubjectProfile,
  fetchDashboardSubjectProfileHistory,
} from "../api/dashboardResults";

/**
 * Loads the demographic profile a patient entered, plus the per-field timeline
 * behind it, for the clinician patient-details view.
 *
 * The two reads are deliberately separate requests: the profile is a single
 * document read and returns immediately, while the history walks the patient's
 * stored profile snapshots and can be noticeably slower. The profile therefore
 * renders as soon as it lands and the history fills the per-field change counts
 * in behind it.
 *
 * Both reads start only once `enabled` becomes true, so opening a patient page
 * costs nothing until a clinician actually asks for the details.
 *
 * @param {{ uid?: string | null, enabled?: boolean }} [options={}] - Target subject and activation flag.
 * @returns {{
 *   subject: object | null,
 *   profile: object | null,
 *   historyFields: Record<string, object[]> | null,
 *   historyTruncated: boolean,
 *   loading: boolean,
 *   historyLoading: boolean,
 *   error: object | null,
 *   historyError: object | null,
 *   reload: () => void
 * }} Demographics state for the overlay.
 */
export function useSubjectDemographics({ uid, enabled = false } = {}) {
  const [subject, setSubject] = useState(null);
  const [profile, setProfile] = useState(null);
  const [historyFields, setHistoryFields] = useState(null);
  const [historyTruncated, setHistoryTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState(null);
  const [historyError, setHistoryError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  useEffect(() => {
    if (!enabled || !uid) {
      return undefined;
    }

    const abortController = new AbortController();

    setSubject(null);
    setProfile(null);
    setHistoryFields(null);
    setHistoryTruncated(false);
    setError(null);
    setHistoryError(null);
    setLoading(true);
    setHistoryLoading(true);

    (async () => {
      try {
        const response = await fetchDashboardSubjectProfile({
          uid,
          signal: abortController.signal,
        });

        if (abortController.signal.aborted) {
          return;
        }

        setSubject(response?.subject || null);
        setProfile(response?.profile || null);
      } catch (caught) {
        if (!abortController.signal.aborted) {
          setError(caught);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    })();

    (async () => {
      try {
        const response = await fetchDashboardSubjectProfileHistory({
          uid,
          signal: abortController.signal,
        });

        if (abortController.signal.aborted) {
          return;
        }

        setHistoryFields(response?.fields || {});
        setHistoryTruncated(Boolean(response?.truncated));
      } catch (caught) {
        if (!abortController.signal.aborted) {
          setHistoryError(caught);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setHistoryLoading(false);
        }
      }
    })();

    return () => {
      abortController.abort();
    };
  }, [enabled, uid, reloadToken]);

  return {
    subject,
    profile,
    historyFields,
    historyTruncated,
    loading,
    historyLoading,
    error,
    historyError,
    reload,
  };
}
